import { useEffect, useState } from 'react';
import {
  Button,
  Link,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { EMBEDDED_DOWNLOAD_EVENT, saveFileInNewWindow, type SavedFile } from './format';

const useStyles = makeStyles({
  dock: {
    position: 'fixed',
    right: tokens.spacingHorizontalL,
    bottom: tokens.spacingVerticalL,
    maxWidth: '520px',
    zIndex: 1000,
    boxShadow: tokens.shadow16,
  },
});

/**
 * Shown after a download while the app is embedded in D365. D365's Website tile may sandbox its
 * frame without allow-downloads, which drops the download silently; "Save in a new window" saves
 * the same file from a top-level window instead.
 */
export function DownloadNotice() {
  const styles = useStyles();
  const [file, setFile] = useState<SavedFile | null>(null);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const onDownload = (e: Event) => {
      setFile((e as CustomEvent<SavedFile>).detail);
      setBlocked(false);
    };
    window.addEventListener(EMBEDDED_DOWNLOAD_EVENT, onDownload);
    return () => window.removeEventListener(EMBEDDED_DOWNLOAD_EVENT, onDownload);
  }, []);

  if (!file) return null;
  const appUrl = window.location.href;
  return (
    <div className={styles.dock}>
      <MessageBar intent={blocked ? 'warning' : 'info'} layout="multiline">
        <MessageBarBody>
          <MessageBarTitle>Downloading {file.fileName}</MessageBarTitle>
          {blocked ? (
            <>
              The new window was blocked too. Open the app on its own and download it there:{' '}
              <Link href={appUrl} target="_blank" rel="noopener">
                {appUrl}
              </Link>
            </>
          ) : (
            'Nothing saved? D365 can block downloads inside its page; save it from a new window instead.'
          )}
        </MessageBarBody>
        <MessageBarActions>
          <Button onClick={() => (saveFileInNewWindow(file) ? setFile(null) : setBlocked(true))}>
            Save in a new window
          </Button>
          <Button appearance="transparent" onClick={() => setFile(null)}>
            Dismiss
          </Button>
        </MessageBarActions>
      </MessageBar>
    </div>
  );
}
